import logger from '../utils/logger';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripeTerminal } from '@stripe/terminal-js';
import GearsSpinner from './customComponents/GearsSpinner';

const TerminalCheckoutScreen = ({ amount, baseAmount, processingFee, patients_id, payment_id, name, email, user, type, allocations, group_ref, chargeDescription }) => {
  const [terminalInstance, setTerminalInstance] = useState(null);
  const [readerId, setReaderId] = useState(null);
  const [readerConnected, setReaderConnected] = useState(false);
  const [status, setStatus] = useState('');
  const [processing, setProcessing] = useState(false);
  const [cancellable, setCancellable] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [terminalChargeFailed, setTerminalChargeFailed] = useState(false);
  const [fundingType, setFundingType] = useState(null); // "credit", "debit", "prepaid", "unknown"
  const [finalChargeAmount, setFinalChargeAmount] = useState(amount);
  const BASE_URL = process.env.REACT_APP_API_URL;
  // const READER_ID = 'tmr_GRwvmguvqj6XCj'; // Your Reader ID
  const READER_ID = 'tmr_GfglaA9qco6BQ2'; // Your Reader ID
  const navigate = useNavigate();

  useEffect(() => {
    initTerminal();
  }, [BASE_URL, READER_ID]);

  const initTerminal = async () => {
    try {
      // logger.log('Loading Stripe Terminal SDK...');
      const terminalFactory = await loadStripeTerminal();
      // logger.log('SDK loaded successfully:', terminalFactory);

      const terminal = terminalFactory.create({
        onFetchConnectionToken: async () => {
          // logger.log('Fetching connection token...');
          try {
            const res = await fetch(`${BASE_URL}/payments/connection_token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
              throw new Error(`HTTP error! Status: ${res.status}`);
            }
            const data = await res.json();
            // logger.log('Connection token received:', data);
            return data.secret;
          } catch (error) {
            logger.error('Fetch connection token error:', error);
            throw error;
          }
        },
        onUnexpectedReaderDisconnect: () => {
          logger.warn('Reader disconnected unexpectedly');
          setReaderConnected(false);
          setStatus('Reader disconnected unexpectedly. Please return to checkout.');
        },
        onConnectionStatusChange: (status) => {
          // logger.log('Connection status changed:', status);
          setReaderConnected(status.status === 'connected');
          setStatus(
            status.status === 'connected'
              ? `Connected to WisePOS E (${READER_ID})`
              : `Reader connection status: ${status.status}. Please ensure the reader is online.`
          );
        },
      });

      setTerminalInstance(terminal);

      // Verify reader status
      // logger.log('Verifying reader status for:', READER_ID);
      try {
        const readerRes = await fetch(`${BASE_URL}/payments/reader/${READER_ID}`);
        if (!readerRes.ok) {
          const errorData = await readerRes.json().catch(() => ({}));
          throw new Error(`Reader status fetch failed: ${readerRes.status}, ${errorData.error || 'Unknown error'}`);
        }
        const { reader } = await readerRes.json();
        // logger.log('Reader status:', reader);

        if (reader.status !== 'online') {
          setStatus('Reader is offline. Ensure WisePOS E is powered on and connected to Wi-Fi.');
          return;
        }

        // Discover readers
        // logger.log('Discovering readers...');
        const discoverResult = await terminal.discoverReaders({
          method: 'internet',
          timeout: 10000, // Set a 10-second timeout for discovery
        });

        if (discoverResult.error) {
          throw new Error(`Discovery failed: ${discoverResult.error.message}`);
        }

        // logger.log('Discovered readers:', discoverResult.discoveredReaders);
        const targetReader = discoverResult.discoveredReaders.find(
          (reader) => reader.id === READER_ID
        );

        if (!targetReader) {
          setStatus(
            `Reader ${READER_ID} not found. Ensure it is powered on, connected to the same network, and registered in Stripe.`
          );
          return;
        }

        // Connect to the reader
        // logger.log('Connecting to reader:', targetReader);
        const connectResult = await terminal.connectReader(targetReader);
        if (connectResult.error) {
          throw new Error(`Connection failed: ${connectResult.error.message}`);
        }
        // logger.log('Reader connected:', connectResult.reader);

        // Verify connection status
        const connectionStatus = await terminal.getConnectionStatus();
        if (connectionStatus !== 'connected') {
          throw new Error(`Reader connection failed. Current status: ${connectionStatus}`);
        }

        setReaderId(READER_ID);
        setReaderConnected(true);
        setStatus(`Connected to WisePOS E (${READER_ID})`);
      } catch (error) {
        logger.error('Reader discovery or connection error:', error);
        setReaderConnected(false);
        setStatus(
          `Failed to discover or connect to reader: ${error.message}. Please ensure WisePOS E is on the same network, powered on, and registered in Stripe, or contact support.`
        );
      }
    } catch (err) {
      logger.error('Terminal init error:', err);
      setReaderConnected(false);
      setStatus(`Failed to initialize Stripe Terminal: ${err.message}. Please return to checkout.`);
    }
  };

  if (!READER_ID || READER_ID === 'tmr_xxx') {
    setStatus('Please set your READER_ID in the code (from Stripe Dashboard > Terminal > Readers).');
    return;
  }

  const capturePayment = async (paymentIntentId) => {
    try {
      const res = await fetch(`${BASE_URL}/payments/capture-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(`Capture failed: ${res.status}, ${errorData.error || 'Unknown error'}`);
      }
      const { paymentIntent } = await res.json();
      setStatus(`✅ Captured: ${paymentIntent.id}`);
      // Record the charge now that it's captured (amount_received is set). Idempotent;
      // splits combined collections via the PaymentIntent metadata on the server.
      try {
        await fetch(`${BASE_URL}/payments/recordCardCharge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId, user, type }),
        });
      } catch (recErr) {
        logger.error('Failed to record terminal charge:', recErr);
      }
      navigate("/successfulPayment");
    } catch (error) {
      logger.error('Capture error:', error);
      setStatus(`Capture failed: ${error.message}. Please return to checkout.`);
    }
  };

  const cancelPayment = async () => {
    if (!paymentIntentId && !cancellable) {
      setStatus('No PaymentIntent to cancel.');
      setProcessing(false);
      setIsCancelled(true);
      return;
    }

    setStatus('Canceling PaymentIntent and reader action...');
    setIsCancelled(true);

    try {
      // Cancel reader action
      if (terminalInstance && readerConnected) {
        // logger.log(`Canceling reader action for ${readerId}`);
        const cancelResult = await terminalInstance.cancelCollectPaymentMethod();
        if (cancelResult.error) {
          logger.warn('Cancel reader action failed:', cancelResult.error.message);
        } else {
          // logger.log('Reader action canceled');
        }
      }

      // Cancel PaymentIntent
      if (paymentIntentId) {
        // logger.log(`Canceling PaymentIntent: ${paymentIntentId}`);
        const cancelPiRes = await fetch(`${BASE_URL}/payments/cancel-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId }),
        });
        if (!cancelPiRes.ok) {
          const errorData = await cancelPiRes.json().catch(() => ({}));
          throw new Error(errorData.error || `Cancel PaymentIntent failed: ${cancelPiRes.status}`);
        }
        const { paymentIntent, message } = await cancelPiRes.json();
        // logger.log('PaymentIntent canceled:', paymentIntent);
        setStatus(`❌ Canceled: ${message}. You can return to checkout.`);
      }
    } catch (err) {
      logger.error('Cancel error:', err);
      setStatus(`Cancel error: ${err.message}. You can return to checkout.`);
    } finally {
      setCancellable(false);
      setPaymentIntentId(null);
      setProcessing(false);
    }
  };

  const handleTerminalPayment = async (retrying = false) => {
    if (retrying) {
      initTerminal();
    }
    if (!terminalInstance || !readerId || !readerConnected) {
      setStatus('Terminal or reader not initialized or connected. Please return to checkout.');
      return;
    }

    // Double-check connection status
    const connectionStatus = await terminalInstance.getConnectionStatus();
    if (connectionStatus !== 'connected') {
      setReaderConnected(false);
      setTerminalChargeFailed(true);
      setStatus(
        `Reader is not connected (status: ${connectionStatus}). Please ensure the reader is online and try again.`
      );
      return;
    }

    setProcessing(true);
    setCancellable(false);
    setIsCancelled(false);
    setStatus('Creating PaymentIntent...');

    try {
      // Step 1: Create PaymentIntent with the base amount (no surcharge yet)
      const res = await fetch(`${BASE_URL}/payments/create-terminal-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: baseAmount, name: name || '', email: email || '', patients_id, payment_id, user, type, allocations: allocations || undefined, group_ref: group_ref || undefined, description: chargeDescription || undefined }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(`HTTP error! Status: ${res.status}, ${errorData.error || 'Unknown error'}`);
      }
      const { clientSecret } = await res.json();
      const piId = clientSecret.split('_secret_')[0];
      setPaymentIntentId(piId);
      setCancellable(true);

      // Step 2: Collect the payment method (customer presents card)
      setStatus('Collecting payment method...');
      const collectResult = await terminalInstance.collectPaymentMethod(clientSecret);
      if (collectResult.error) {
        throw new Error(`Collect payment failed: ${collectResult.error.message}`);
      }

      // Step 3: Check funding type from the collected payment method
      const paymentMethodDetails = collectResult.paymentIntent?.payment_method_details?.card_present
        || collectResult.paymentIntent?.payment_method?.card_present;
      const funding = paymentMethodDetails?.funding;
      setFundingType(funding);

      const isCreditCard = funding === 'credit';
      const chargeAmount = isCreditCard ? amount : baseAmount; // amount already includes fee
      setFinalChargeAmount(chargeAmount);

      // Step 4: If credit card and surcharge applies, update the PI amount on the server
      if (isCreditCard && processingFee > 0) {
        setStatus('Applying processing fee for credit card...');
        const updateRes = await fetch(`${BASE_URL}/payments/update-payment-intent-amount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: piId,
            amount: chargeAmount,
            surcharge_amount: processingFee,
            original_amount: baseAmount
          }),
        });
        if (!updateRes.ok) {
          const errorData = await updateRes.json().catch(() => ({}));
          throw new Error(`Failed to update amount: ${updateRes.status}, ${errorData.error || 'Unknown error'}`);
        }
      }

      // Step 5: Process the payment
      setStatus('Processing payment...');
      const processResult = await terminalInstance.processPayment(collectResult.paymentIntent);
      if (processResult.error) {
        throw new Error(`Process payment failed: ${processResult.error.message}`);
      }

      setStatus('✅ Payment succeeded');
      setCancellable(false);
      await capturePayment(piId);
      navigate("/successfulPayment");
    } catch (err) {
      logger.error('Payment error:', err);
      setStatus(`Payment error: ${err.message}. Please return to checkout.`);
      setCancellable(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (processing) {
      setStatus('Cannot go back while processing. Please cancel the payment first.');
      return;
    }
    // Prefer returning to the amount/method screen if a handler was provided.
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    try {
      navigate(-1);
    } catch (error) {
      logger.error('Navigation error:', error);
      navigate('/hgiPayments', { replace: true });
    }
  };

  return (
    <div className="terminal-checkout-screen">
      <h2>Terminal Payment</h2>
      {processingFee > 0 && (
        <div className="fee-breakdown">
          <div><strong>Subtotal:</strong> ${(baseAmount / 100).toFixed(2)}</div>
          <div><strong>Processing Fee (2.7%):</strong> ${(processingFee / 100).toFixed(2)}</div>
          <div><strong>Total:</strong> ${(amount / 100).toFixed(2)}</div>
          {fundingType && fundingType !== 'credit' ? (
            <div className="fee-notice">No processing fee — {fundingType} card detected. You will only be charged ${(baseAmount / 100).toFixed(2)}.</div>
          ) : (
            <div className="fee-notice">* Processing fee applies to credit cards only. Debit cards are not surcharged.</div>
          )}
        </div>
      )}
      {!processingFee && (
        <p><strong>Amount: ${(baseAmount / 100).toFixed(2)}</strong></p>
      )}

      {(!readerConnected || processing) && (
        <div className="loading-container">
          <GearsSpinner alt="Loading..." />
          <p>{status || 'Connecting to reader...'}</p>
        </div>
      )}

      {cancellable && processing && (
        <button
          onClick={cancelPayment}
          className='PrimaryButton'
        >
          Cancel Payment
        </button>
      )}

      <button
        onClick={handleBack}
        disabled={processing}
        className='PrimaryButton'
      >
        Back to HGI Payments
      </button>

      {readerConnected && !processing && (
        <button onClick={() => {handleTerminalPayment()}} className="PrimaryButton">
          Charge via Terminal
        </button>
      )}

      {terminalChargeFailed && !readerConnected && (
        <button onClick={() => {handleTerminalPayment(true)}} className="PrimaryButton">
          Retry
        </button>
      )}
      
      {/* {readerConnected && !processing && (
        <>
        <Link to={"/home"}><button className='PrimaryButton'>Home</button></Link>
        <button onClick={handleTerminalPayment} className="PrimaryButton">
          Charge via Terminal
        </button>
        
        </>
      )} */}
    </div>
  );
};

export default TerminalCheckoutScreen;